import React, { memo } from 'react';
import Clickable from '@prisjakt/ui-clickable';

import { NotificationType, NotificationTypeEnum } from './types';

import * as S from './styles';
import { layout } from '@prisjakt/ui-base';
import { IconChevronRight, IconClose } from '@prisjakt/ui/icon';

type Issue = {
  messages: {
    id: string | number;
    time?: string | null;
    title?: string;
    message?: string | null;
  }[];
};

type Info = {
  message: string;
  link?: {
    text: string;
    url: string;
    icon?: React.ReactElement;
    isExternal?: boolean;
  };
};

type Props = {
  className?: string;
  notification: Issue | Info;
  type: NotificationType;
  onClose?: () => void;
};

const Notification = ({ className, notification, type, onClose }: Props) => {
  const renderIssue = () => {
    return (notification as Issue).messages.map(
      ({ id, message, time, title }) =>
        message && (
          <S.Message key={id}>
            <S.Header>
              {title && <S.Title>{title}</S.Title>}

              {time && (
                <>
                  <S.Divider />
                  <S.Date>{time}</S.Date>
                </>
              )}
            </S.Header>
            <S.MessageText dangerouslySetInnerHTML={{ __html: message }} />
          </S.Message>
        )
    );
  };

  const renderInfo = () => {
    const item = notification as Info;

    return item.link?.url ? (
      <Clickable to={item.link.url} isExternal={item.link.isExternal}>
        <S.Message>
          <p>{item.message}</p>
          {item.link?.text && (
            <S.LinkText>
              {item.link?.icon && item.link.icon}
              <S.Text
                {...(item.link?.icon && {
                  style: { marginLeft: layout.measurements.spacing025 },
                })}
              >
                {item.link.text}
              </S.Text>
              <IconChevronRight size={20} color="maybeBlue700" />
            </S.LinkText>
          )}
        </S.Message>
      </Clickable>
    ) : (
      <S.Message>
        <p>{item.message}</p>

        {onClose && (
          <S.CloseButton
            variant="icon"
            icon={<IconClose />}
            size="small"
            color="primary"
            onClick={onClose}
          />
        )}
      </S.Message>
    );
  };

  const renderNeutral = () => {
    return (notification as Issue).messages.map(
      ({ id, message, time, title }) =>
        message && (
          <S.Message key={id}>
            <S.Header>
              {title && <S.Title>{title}</S.Title>}

              {time && (
                <>
                  <S.Divider />
                  <S.Date>{time}</S.Date>
                </>
              )}
            </S.Header>
            <S.MessageText dangerouslySetInnerHTML={{ __html: message }} />
          </S.Message>
        )
    );
  };

  return (
    <S.Notification type={type} className={className}>
      {type === NotificationTypeEnum.INFO && renderInfo()}
      {type === NotificationTypeEnum.ISSUE && renderIssue()}
      {type === NotificationTypeEnum.NEUTRAL && renderNeutral()}
    </S.Notification>
  );
};

export default memo(Notification);
